import { useStudyText } from '@/i18n/studyText';
import { studentApi } from '../api';
import { CourseTeams } from '@/features/course/CourseTeams';
import { useState, type FC } from 'react';
import type {
  StudentAssignment,
  StudentAttendanceLogEntry,
  StudentAttendanceOverview,
  StudentDiscipline,
  StudentDocumentTemplate,
  StudentGradeControlPoint,
  StudentGradeItem,
  StudentGradeOverview,
  StudentLessonWeek,
  StudentMeeting,
  StudentPortfolioItem,
  StudentProfile,
  StudentRequest,
} from '../types';
import {
  BookIcon,
  CalendarIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  DownloadIcon,
  FileTextIcon,
  MessageSquareIcon,
  StarIcon,
  UsersIcon,
} from './CourseStudentIcons';

interface StudentCourseWorkspaceProps {
  profile: StudentProfile;
  disciplines: StudentDiscipline[];
  selectedDisciplineCode: string;
  onSelectDiscipline: (code: string) => void;
  weeks: StudentLessonWeek[];
  assignments: StudentAssignment[];
  grades: StudentGradeOverview;
  attendance: StudentAttendanceOverview;
  templates: StudentDocumentTemplate[];
  meetings: StudentMeeting[];
  portfolioItems: StudentPortfolioItem[];
  requests: StudentRequest[];
  onBackToCabinet: () => void;
  onOpenUploadModal: (assignment: StudentAssignment) => void;
  onOpenTeacherRequestModal: () => void;
  onOpenDocumentModal: (template: StudentDocumentTemplate) => void;
  onTogglePortfolio: (title: string, workType: string, filename?: string) => void;
}

type WorkspaceTab =
  | 'overview'
  | 'disciplines'
  | 'lessons'
  | 'assignments'
  | 'grades'
  | 'attendance'
  | 'documents'
  | 'team-projects'
  | 'calendar'
  | 'contact-teacher'
  | 'portfolio';

export const StudentCourseWorkspace: FC<StudentCourseWorkspaceProps> = ({
  profile,
  disciplines,
  selectedDisciplineCode,
  onSelectDiscipline,
  weeks,
  assignments,
  grades,
  attendance,
  templates,
  meetings,
  portfolioItems,
  requests,
  onBackToCabinet,
  onOpenUploadModal,
  onOpenTeacherRequestModal,
  onOpenDocumentModal,
  onTogglePortfolio,
}) => {
  const st = useStudyText();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('overview');
  const [selectedComment, setSelectedComment] = useState<string | null>(null);

  const activeDiscipline =
    disciplines.find((d) => d.code === selectedDisciplineCode) || disciplines[0];

  const navItems = [
    { id: 'overview' as const, label: 'Обзор', icon: BookIcon },
    { id: 'disciplines' as const, label: 'Дисциплины', icon: FileTextIcon },
    { id: 'lessons' as const, label: 'Занятия и материалы', icon: FileTextIcon },
    { id: 'assignments' as const, label: 'Задания', icon: CheckCircleIcon },
    { id: 'grades' as const, label: 'Оценки и РК', icon: StarIcon },
    { id: 'attendance' as const, label: 'Посещаемость', icon: CalendarIcon },
    { id: 'documents' as const, label: 'Образцы и документы', icon: FileTextIcon },
    { id: 'team-projects' as const, label: 'Командные проекты', icon: UsersIcon },
    { id: 'calendar' as const, label: 'Календарь', icon: CalendarIcon },
    { id: 'contact-teacher' as const, label: 'Связь с преподавателем', icon: MessageSquareIcon },
    { id: 'portfolio' as const, label: 'Мои работы / портфолио', icon: StarIcon },
  ];

  const nearestAssignment =
    assignments.find((a) => a.status === 'На проверке' || a.status === 'Опубликовано') ||
    assignments[0];

  const gradeList: StudentGradeItem[] =
    grades.items ||
    (grades.breakdown || []).map((b) => ({
      id: b.id,
      title: b.work_title,
      score: b.score,
      max_points: b.max_score,
      status: 'Оценено',
      assignment_type: b.work_type,
      deadline: `Неделя ${b.week_number}`,
      feedback: b.teacher_feedback,
    }));

  const controlPointsList: StudentGradeControlPoint[] =
    grades.control_points || [];

  const attendanceList: StudentAttendanceLogEntry[] =
    attendance.logs ||
    (attendance.records || []).map((r, i) => ({
      id: r.id,
      discipline_code: r.discipline_code,
      lesson_date: r.lesson_date,
      lesson_number: i + 1,
      lesson_title: r.lesson_title,
      lesson_type: r.lesson_title || 'Занятие',
      week_number: r.week_number,
      date_label: r.date_label || r.lesson_date || '',
      status: r.status === 'present' ? 'Присутствовал' : r.status === 'valid_excused' ? 'Уважительная' : 'Отсутствовал',
    }));

  return (
    <div className="space-y-6 pb-12">
      <div className="border border-slate-800 bg-[#081321] rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBackToCabinet}
            className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-700/80 hover:border-cyan-400 text-slate-300 hover:text-cyan-400 flex items-center justify-center transition-all"
            aria-label={st("Вернуться в кабинет")}
          >
            &larr;
          </button>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold">
              {" " + st("Кабинет обучающегося") + " "}</p>
            <h2 className="text-xl font-bold text-white tracking-wide">{st("Обучающийся")}</h2>
            <p className="text-xs text-slate-400 hidden sm:block">
              {" " + st("Дисциплины · материалы · задания · оценки · посещаемость · календарь · работы и учебные документы.") + " "}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-end md:self-auto">
          <button
            type="button"
            onClick={onOpenTeacherRequestModal}
            className="px-4 py-2 rounded-xl border border-cyan-500/50 bg-[#0C1A2B] hover:bg-cyan-950/40 text-cyan-300 text-xs font-semibold transition-all shadow-md"
          >
            {" " + st("Связь с преподавателем") + " "}</button>
          <button
            type="button"
            onClick={onBackToCabinet}
            className="px-5 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md shadow-amber-500/10"
          >
            {st('Сохранить')}
          </button>
        </div>
      </div>

      <div className="border border-slate-800/80 bg-[#08121E] rounded-2xl p-4">
        <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-semibold mb-2">
          {" " + st("АКТИВНАЯ ДИСЦИПЛИНА · 2026–2027 · ОСЕННИЙ СЕМЕСТР") + " "}</p>
        <div className="flex flex-wrap gap-2">
          {disciplines.map((d) => (
            <button
              key={d.code}
              type="button"
              onClick={() => onSelectDiscipline(d.code)}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
                d.code === selectedDisciplineCode
                  ? 'bg-cyan-950/80 border-cyan-400 text-cyan-300 shadow-md shadow-cyan-900/20'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700'
              }`}
            >
              {d.code} · {d.title}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-4">
          <div className="border border-cyan-500/30 bg-[#081524] rounded-2xl p-4 shadow-lg">
            <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold mb-1">
              Личный кабинет слушателя
            </p>
            <h3 className="text-sm font-bold text-white">{profile.full_name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {profile.student_code} · {profile.program_name}
            </p>
            <p className="text-xs text-slate-400">
              {profile.course_year} {" " + st("курс ·") + " "}{profile.group_code}
            </p>
          </div>

          <nav className="border border-slate-800/80 bg-[#08121E] rounded-2xl p-2 space-y-1 shadow-lg">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`workspace-tab-${item.id}`}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-cyan-950/70 border border-cyan-500/50 text-cyan-300 shadow-md'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/60'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className={`w-4 h-4 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                    <div className="text-left">
                      <p>{st(item.label)}</p>
                      <p className="text-[10px] font-normal text-slate-400">{st("Учебный раздел")}</p>
                    </div>
                  </div>
                  <ChevronRightIcon className={`w-3.5 h-3.5 ${isActive ? 'text-cyan-400' : 'text-slate-600'}`} />
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="lg:col-span-9 space-y-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-[#091524] border border-slate-800/80">
                  <p className="text-[11px] text-slate-400">{st("дисциплины")}</p>
                  <p className="text-xl font-bold text-white mt-1">3</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{st("текущий семестр")}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#091524] border border-slate-800/80">
                  <p className="text-[11px] text-slate-400">{st("сегодня")}</p>
                  <p className="text-xl font-bold text-white mt-1">2</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{st("занятия")}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#091524] border border-slate-800/80">
                  <p className="text-[11px] text-slate-400">{st("дедлайны")}</p>
                  <p className="text-xl font-bold text-white mt-1">1</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{st("требуют действия")}</p>
                </div>
                <div className="p-4 rounded-xl bg-[#091524] border border-slate-800/80">
                  <p className="text-[11px] text-slate-400">{st("мой прогресс")}</p>
                  <p className="text-xl font-bold text-cyan-400 mt-1">90%</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{st("по выбранной дисциплине")}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8 p-5 rounded-2xl bg-[#081524] border border-cyan-500/30 space-y-4">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                      {" " + st("УЧЕБНЫЙ РАЗДЕЛ · МОЯ ДИСЦИПЛИНА") + " "}</p>
                    <h3 className="text-xl font-bold text-white mt-1">
                      {activeDiscipline?.title || 'Современный казахский язык'}
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {activeDiscipline?.code} · {profile.student_code} · {profile.program_name} {" " + st("· преподаватель:") + " "}{activeDiscipline?.teacher_name}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                    <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-[10px] text-slate-400">{st("Текущий балл")}</p>
                      <p className="text-lg font-bold text-white mt-0.5">{activeDiscipline?.current_score ?? 0}</p>
                      <p className="text-[10px] text-slate-400">{st("из 100")}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-[10px] text-slate-400">{st("Посещаемость")}</p>
                      <p className="text-lg font-bold text-emerald-400 mt-0.5">{grades.attendance_rate ?? 0}%</p>
                      <p className="text-[10px] text-slate-400">{st("текущий семестр")}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-[10px] text-slate-400">{st("Задания")}</p>
                      <p className="text-lg font-bold text-white mt-0.5">2</p>
                      <p className="text-[10px] text-slate-400">{st("по дисциплине")}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-[10px] text-slate-400">{st("На проверке")}</p>
                      <p className="text-lg font-bold text-amber-400 mt-0.5">1</p>
                      <p className="text-[10px] text-slate-400">{st("ожидают решения")}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setActiveTab('lessons')}
                      className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-all"
                    >
                      {" " + st("Показать занятия") + " "}</button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('assignments')}
                      className="px-3.5 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md shadow-amber-500/10"
                    >
                      {st('Открыть задания')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('documents')}
                      className="px-3.5 py-2 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 text-xs font-semibold transition-all"
                    >
                      {" " + st("Образцы и документы") + " "}</button>
                  </div>
                </div>

                <div className="lg:col-span-4 p-5 rounded-2xl bg-[#081524] border border-amber-500/40 space-y-3 flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">
                      {" " + st("БЛИЖАЙШИЙ ДЕДЛАЙН") + " "}</p>
                    <h4 className="text-base font-bold text-white mt-1">{st("Следующее действие")}</h4>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {" " + st("Карточка сокращена специально для боковой области: без обрезки рубрики и кнопок.") + " "}</p>

                    {nearestAssignment && (
                      <div className="mt-4 p-3 rounded-xl bg-slate-900/90 border border-slate-800 space-y-2">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-amber-950 text-amber-400 border border-amber-800/60">
                          {st(nearestAssignment.status)}
                        </span>
                        <h5 className="text-xs font-bold text-slate-200">{nearestAssignment.title}</h5>
                        <p className="text-[11px] text-slate-400">
                          {" " + st("СРО · неделя") + " "}{nearestAssignment.week_number} {" " + st("· до") + " "}{nearestAssignment.deadline} · {nearestAssignment.max_points} {" " + st("баллов.") + " "}</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-3">
                    {nearestAssignment && (
                      <button
                        type="button"
                        onClick={() => onOpenUploadModal(nearestAssignment)}
                        className="w-full py-2.5 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md"
                      >
                        {" " + st("Загрузить новую версию") + " "}</button>
                    )}
                    <button
                      type="button"
                      onClick={() => setActiveTab('assignments')}
                      className="w-full py-2 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 text-xs font-semibold transition-all"
                    >
                      {st('Открыть задание')}
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                    {" " + st("КОНТРОЛЬНЫЕ ТОЧКИ СЕМЕСТРА") + " "}</p>
                  <h4 className="text-base font-bold text-white mt-0.5">
                    {" " + st("РК1 · РК2 · итоговый контроль") + " "}</h4>
                  <p className="text-xs text-slate-400">
                    {" " + st("Контрольные точки видны заранее и связаны с оценками дисциплины.") + " "}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800/80">
                    <p className="text-[10px] text-amber-400 font-semibold uppercase">{st("КОНТРОЛЬНАЯ ТОЧКА · 7 неделя")}</p>
                    <h5 className="text-sm font-bold text-white mt-1">{st("Рубежный контроль 1")}</h5>
                    <p className="text-xs text-slate-400 mt-0.5">{st("Запланировано на 7 неделя.")}</p>
                    <span className="inline-block mt-3 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                      {" " + st("Запланировано") + " "}</span>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800/80">
                    <p className="text-[10px] text-purple-400 font-semibold uppercase">{st("КОНТРОЛЬНАЯ ТОЧКА · 15 неделя")}</p>
                    <h5 className="text-sm font-bold text-white mt-1">{st("Рубежный контроль 2")}</h5>
                    <p className="text-xs text-slate-400 mt-0.5">{st("Запланировано на 15 неделя.")}</p>
                    <span className="inline-block mt-3 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                      {" " + st("Запланировано") + " "}</span>
                  </div>

                  <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800/80">
                    <p className="text-[10px] text-emerald-400 font-semibold uppercase">{st("ИТОГОВЫЙ КОНТРОЛЬ")}</p>
                    <h5 className="text-sm font-bold text-white mt-1">{st("Итоговый контроль / экзамен")}</h5>
                    <p className="text-xs text-slate-400 mt-0.5">{st("После учебных недель дисциплины.")}</p>
                    <span className="inline-block mt-3 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                      {" " + st("Запланировано") + " "}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'disciplines' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white">{st("Дисциплины текущего семестра")}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {disciplines.map((d) => (
                  <div
                    key={d.code}
                    className={`p-4 rounded-xl border transition-all ${
                      d.code === selectedDisciplineCode
                        ? 'bg-[#081524] border-cyan-400'
                        : 'bg-[#08121E] border-slate-800'
                    }`}
                  >
                    <span className="text-xs font-mono text-cyan-400 font-bold">{d.code}</span>
                    <h4 className="text-sm font-bold text-white mt-1">{d.title}</h4>
                    <p className="text-xs text-slate-400 mt-1">{"" + st("Преподаватель:") + " "}{d.teacher_name}</p>
                    <p className="text-xs text-slate-400">{"" + st("Кредиты:") + " "}{d.credits} · {d.degree_level}</p>
                    <div className="mt-4 pt-3 border-t border-slate-800 flex justify-between items-center text-xs">
                      <span className="text-emerald-400 font-semibold">{"" + st("Балл:") + " "}{d.current_score}</span>
                      <button
                        type="button"
                        onClick={() => onSelectDiscipline(d.code)}
                        className="px-3 py-1 rounded-lg bg-cyan-950 hover:bg-cyan-900 text-cyan-300 border border-cyan-700/60 font-medium"
                      >
                        {" " + st("Выбрать") + " "}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'lessons' && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white">
                {" " + st("Занятия и материалы:") + " "}{activeDiscipline?.title}
              </h3>
              <div className="space-y-3">
                {weeks.map((w) => (
                  <div key={w.id} className="p-4 rounded-xl bg-[#08121E] border border-slate-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-cyan-400 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800/40">
                        {" " + st("Неделя") + " "}{w.week_number}
                      </span>
                      <span className="text-xs text-slate-400">{w.srop_window || st("Окно консультации не задано")}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800/60">
                        <p className="text-[10px] text-cyan-400 font-bold uppercase">{st("Лекция")}</p>
                        <h4 className="text-xs font-bold text-white mt-1">{w.lecture_title || w.lecture_topic || w.title}</h4>
                        <p className="text-xs text-slate-400 mt-1">{w.lecture_description || w.independent_work_topic}</p>
                        {w.lecture_file_url && <button onClick={() => void studentApi.downloadCourseFile(w.lecture_file_url!)}>{st('Скачать лекцию')}</button>}
                      </div>
                      <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800/60">
                        <p className="text-[10px] text-amber-400 font-bold uppercase">{st("Практика / Семинар")}</p>
                        <h4 className="text-xs font-bold text-white mt-1">{w.practice_title || w.seminar_topic}</h4>
                        <p className="text-xs text-slate-400 mt-1">{w.practice_description || w.title}</p>
                        {w.practice_file_url && <button onClick={() => void studentApi.downloadCourseFile(w.practice_file_url!)}>{st('Скачать практику')}</button>}
                        <p>{"" + st("СРО:") + " "}{w.sro_title} · {w.sro_description} · {w.sro_deadline || st("Срок не задан")}</p>
                        <p>{"" + st("СРОП:") + " "}{w.srop_title} · {w.srop_description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'assignments' && (
            <div className="space-y-5">
              <div className="p-4 rounded-2xl bg-[#081524] border border-amber-500/40">
                <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">{st("МОИ ЗАДАНИЯ")}</p>
                <h3 className="text-xl font-bold text-white mt-1">{activeDiscipline?.title}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {" " + st("Нужно выполнить · отправлено · на проверке · возвращено на доработку · повторная сдача · оценено. Все версии работы сохраняются и открываются без обрезки.") + " "}</p>
              </div>

              <div className="space-y-4">
                {assignments.map((assign) => (
                  <div
                    key={assign.id}
                    className="p-5 rounded-2xl bg-[#081321] border border-slate-800 hover:border-slate-700 transition-all space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div>
                        <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">
                          {assign.assignment_type}
                        </span>
                        <h4 className="text-base font-bold text-white mt-0.5">{assign.title}</h4>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {" " + st("Дедлайн:") + " "}{assign.deadline} {" " + st("· максимум") + " "}{assign.max_points} {" " + st("баллов.") + " "}</p>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                          {" " + st("Опубликовано") + " "}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                            assign.status === 'Оценено'
                              ? 'bg-emerald-950 text-emerald-300 border-emerald-700/60'
                              : 'bg-amber-950 text-amber-300 border-amber-700/60'
                          }`}
                        >
                          {st(assign.status)}
                        </span>
                      </div>
                    </div>

                    {assign.rubric_criteria && assign.rubric_criteria.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-800/80">
                        {assign.rubric_criteria.map((crit) => (
                          <div key={crit.id} className="text-xs">
                            <span className="text-slate-400">{crit.title}</span>:{' '}
                            <span className="font-bold text-slate-200">{crit.max_points} {" " + st("б.") + ""}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {assign.rubric_json && !assign.rubric_criteria && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-800/80">
                        {Object.entries(assign.rubric_json).map(([crit, pts]) => (
                          <div key={crit} className="text-xs">
                            <span className="text-slate-400">{crit}</span>:{' '}
                            <span className="font-bold text-slate-200">{pts} {" " + st("б.") + ""}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="text-xs space-y-1">
                      {assign.submission_history?.map(version => <div key={version.id}>{"" + st("Версия") + " "}{version.version} · {st(version.status)} · {version.score ?? '—'} / {assign.max_points}
                        {version.file_url && <button onClick={() => void studentApi.downloadCourseFile(version.file_url!)}> {st('Скачать')}</button>}
                        <p>{version.feedback}</p><p>{version.feedback_note}</p>
                      </div>)}
                    </div>
                    <div className="pt-2 border-t border-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="text-xs text-slate-400">
                        {assign.my_submission?.score !== null && assign.my_submission?.score !== undefined ? (
                          <span className="text-lg font-bold text-white">
                            {assign.my_submission.score} <span className="text-xs text-slate-400">/ {assign.max_points}</span>
                          </span>
                        ) : (
                          <span>{assign.my_submission ? `Версия ${assign.my_submission.version}: ${st(assign.my_submission.status)}` : st('Работа еще не сдана')}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {assign.my_submission?.feedback && (
                          <button
                            type="button"
                            onClick={() => setSelectedComment(assign.my_submission?.feedback || null)}
                            className="px-3 py-1.5 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 text-xs font-semibold transition-all"
                          >
                            {" " + st("Комментарий") + " "}</button>
                        )}
                        {assign.my_submission && <button onClick={() => onTogglePortfolio(assign.title, assign.assignment_type)}>{st("В портфолио")}</button>}
                        <button
                          type="button"
                          disabled={assign.publication_status === 'Закрыто'}
                          onClick={() => onOpenUploadModal(assign)}
                          className="px-4 py-1.5 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md"
                        >
                          {" " + st("Загрузить новую версию") + " "}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'grades' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-5 p-5 rounded-2xl bg-[#081524] border border-cyan-500/40 space-y-4">
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">{st("МОЙ ПРОГРЕСС")}</p>
                  <h3 className="text-lg font-bold text-white">{activeDiscipline?.title}</h3>
                  <div>
                    <span className="text-4xl font-bold text-white">
                      {grades.current_score ?? grades.total_rating ?? 0}
                    </span>
                    <span className="text-xs text-slate-400 ml-2">
                      {" " + st("текущий балл из") + " "}{grades.max_score ?? 100}
                    </span>
                  </div>

                  <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      className="h-full bg-cyan-400 rounded-full"
                      style={{ width: `${grades.current_score ?? grades.total_rating ?? 0}%` }}
                    ></div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-2 text-xs">
                    <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-slate-400 text-[10px]">{st("Практика")}</p>
                      <p className="font-bold text-white text-base mt-0.5">{grades.practice_rate ?? 90}%</p>
                      <p className="text-[10px] text-slate-400">{st("текущий вклад")}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-slate-400 text-[10px]">{st("СРО")}</p>
                      <p className="font-bold text-white text-base mt-0.5">{grades.sro_rate ?? 0}%</p>
                      <p className="text-[10px] text-slate-400">{st("текущий вклад")}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-slate-400 text-[10px]">{st("Рубежный контроль")}</p>
                      <p className="font-bold text-white text-base mt-0.5">
                        {grades.control_rate ?? `${grades.attestation_1 ?? 0} / ${grades.attestation_2 ?? 0}`}
                      </p>
                      <p className="text-[10px] text-slate-400">{st("по контрольным точкам")}</p>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-slate-400 text-[10px]">{st("Посещаемость")}</p>
                      <p className="font-bold text-emerald-400 text-base mt-0.5">{grades.attendance_rate ?? 0}%</p>
                      <p className="text-[10px] text-slate-400">{st("занятия")}</p>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-800">
                    {grades.formula_text ?? st("Рейтинг допуска (РК1 + РК2) рассчитывается согласно силлабусу: (РК1 + РК2)/2 >= 50")}
                  </p>
                </div>

                <div className="lg:col-span-7 p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">{st("ОЦЕНКИ ПО РАБОТАМ")}</p>
                  <h3 className="text-lg font-bold text-white">{st("Из чего сформирован балл")}</h3>

                  <div className="space-y-3">
                    {gradeList.map((it) => (
                      <div key={it.id} className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-bold text-white">{it.title}</h4>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                              it.status === 'Оценено'
                                ? 'bg-emerald-950 text-emerald-300 border-emerald-700/60'
                                : 'bg-amber-950 text-amber-300 border-amber-700/60'
                            }`}
                          >
                            {it.score !== null ? `${it.score}/${it.max_points}` : ''} {st(it.status)}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          {it.assignment_type} {" " + st("· дедлайн") + " "}{it.deadline} {it.feedback ? `· ${it.feedback}` : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-4">
                <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">{st("РУБЕЖНЫЙ И ИТОГОВЫЙ КОНТРОЛЬ")}</p>
                <h4 className="text-base font-bold text-white">{st("Рейтинг допуска (РК1 + РК2)")}</h4>
                <p className="text-xs text-slate-400">
                  {" " + st("РК1 — 7 неделя, РК2 — 15 неделя, далее итоговый контроль / экзамен.") + " "}</p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                  {controlPointsList.map((cp) => (
                    <div key={cp.title} className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
                      <p className="text-[10px] text-cyan-400 font-bold uppercase">{cp.week_label}</p>
                      <h5 className="text-sm font-bold text-white mt-1">{cp.title}</h5>
                      <p className="text-xs text-slate-400 mt-1">{cp.description}</p>
                      <span className="inline-block mt-3 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                        {st(cp.status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'attendance' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-5">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">{st("ПОСЕЩАЕМОСТЬ")}</p>
                <h3 className="text-xl font-bold text-white mt-1">{st("Журнал посещаемости занятий")}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {" " + st("Присутствие, отсутствие, опоздание и возможная отработка фиксируются по занятиям.") + " "}</p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
                  <p className="text-[10px] text-slate-400">{st("Посещаемость")}</p>
                  <p className="text-2xl font-bold text-emerald-400 mt-1">{attendance.attendance_rate ?? attendance.percent ?? 0}%</p>
                  <p className="text-[10px] text-slate-400">{st("текущий показатель")}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
                  <p className="text-[10px] text-slate-400">{st("Учтено занятий")}</p>
                  <p className="text-2xl font-bold text-white mt-1">{attendance.accounted_sessions ?? (attendance.records?.length || 0)}</p>
                  <p className="text-[10px] text-slate-400">{st("период")}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
                  <p className="text-[10px] text-slate-400">{st("Пропуски")}</p>
                  <p className="text-2xl font-bold text-white mt-1">{attendance.absences_count ?? attendance.absent_count ?? 0}</p>
                  <p className="text-[10px] text-slate-400">{st("зафиксировано")}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800">
                  <p className="text-[10px] text-slate-400">{st("Опоздания")}</p>
                  <p className="text-2xl font-bold text-white mt-1">{attendance.lates_count ?? attendance.valid_excused_count ?? 0}</p>
                  <p className="text-[10px] text-slate-400">{st("зафиксировано")}</p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                {attendanceList.map((log) => (
                  <div
                    key={log.id}
                    className="p-3.5 rounded-xl bg-slate-900/80 border border-slate-800 flex items-center justify-between"
                  >
                    <div>
                      <h4 className="text-xs font-bold text-white">
                        {log.lesson_type || log.lesson_title || st("Занятие")}
                      </h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {" " + st("Неделя") + " "}{log.week_number} · {log.date_label}
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-emerald-950 text-emerald-400 border border-emerald-800/60">
                      {st(log.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-5">
              <div className="p-4 rounded-2xl bg-[#081524] border border-cyan-500/40">
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">
                  {" " + st("ОБРАЗЦЫ И ДОКУМЕНТЫ · ВНУТРИ УЧЕБНОГО РАЗДЕЛА") + " "}</p>
                <h3 className="text-xl font-bold text-white mt-1">
                  {" " + st("Как оформлять и сдавать учебные работы") + " "}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {" " + st("Курсовая работа, презентация, эссе, СРО, библиография и актуальные документы дисциплины собраны в одном месте.") + " "}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="p-4 rounded-2xl bg-[#08121E] border border-slate-800 hover:border-slate-700 transition-all space-y-3 flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">
                        {tpl.category}
                      </span>
                      <h4 className="text-sm font-bold text-white mt-1">{tpl.title}</h4>
                      <p className="text-xs text-slate-400 mt-1">{tpl.description}</p>
                    </div>
                    <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">
                        {tpl.file_type} · {tpl.size_label}
                      </span>
                      <button
                        type="button"
                        onClick={() => onOpenDocumentModal(tpl)}
                        className="px-3.5 py-1.5 rounded-xl border border-cyan-500/50 hover:bg-cyan-950/40 text-cyan-300 text-xs font-semibold transition-all"
                      >
                        {st('Открыть образец')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'team-projects' && <CourseTeams code={selectedDisciplineCode} />}

          {activeTab === 'calendar' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-5">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">{st("КАЛЕНДАРЬ")}</p>
                <h3 className="text-xl font-bold text-white mt-1">{st("Занятия, дедлайны, СРОП и проекты")}</h3>
              </div>

              <div className="space-y-3">
                {meetings.map((m) => (
                  <div
                    key={m.id}
                    className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div>
                      <span className="text-xs font-mono font-bold text-cyan-400">{m.scheduled_at}</span>
                      <h4 className="text-sm font-bold text-white mt-0.5">{m.title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {m.meeting_type} · {m.location_or_url}
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-800 text-slate-300 border border-slate-700 self-start sm:self-auto">
                      {st(m.status)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact-teacher' && (
            <div className="p-5 rounded-2xl bg-[#08121E] border border-slate-800 space-y-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">{st("ОБРАЩЕНИЯ")}</p>
                  <h3 className="text-xl font-bold text-white mt-1">{st("Связь с преподавателем")}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {" " + st("Преподаватель:") + " "}{activeDiscipline?.teacher_name || '—'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onOpenTeacherRequestModal}
                  className="px-4 py-2 rounded-xl bg-[#E5A83B] hover:bg-[#D4982E] text-slate-950 text-xs font-bold transition-all shadow-md self-start sm:self-auto"
                >
                  {" " + st("Написать вопрос") + " "}</button>
              </div>

              <div className="space-y-3">
                {requests.length === 0 ? (
                  <p className="text-xs text-slate-400 py-4">{st("Нет активных обращений по данной дисциплине.")}</p>
                ) : (
                  requests.map((r) => (
                    <div key={r.id} className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-bold text-white">{r.title}</h4>
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-800 text-slate-300 border border-slate-700">
                          {st(r.status)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-300">{r.message}</p>
                      {r.teacher_reply && (
                        <div className="mt-2 p-2.5 rounded-lg bg-cyan-950/40 border border-cyan-800/40 text-xs text-cyan-200">
                          <span className="font-bold">{"" + st("Ответ преподавателя:") + " "}</span>
                          {r.teacher_reply}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'portfolio' && (
            <div className="space-y-5">
              <div className="p-4 rounded-2xl bg-[#081524] border border-purple-500/40">
                <p className="text-[10px] uppercase tracking-wider text-purple-400 font-bold">{st("МОИ РАБОТЫ / ПОРТФОЛИО")}</p>
                <h3 className="text-xl font-bold text-white mt-1">{st("Учебные и выбранные внеучебные результаты")}</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {" " + st("Отправленные работы, проекты и презентации сохраняются по годам. Отдельно можно добавить научный проект в академическое портфолио.") + " "}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {portfolioItems.map((p) => (
                  <div
                    key={p.title}
                    className="p-4 rounded-2xl bg-[#08121E] border border-slate-800 hover:border-slate-700 transition-all space-y-3 flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-amber-400 uppercase">
                          {p.is_starred ? st("★ ПОРТФОЛИО") : st("МОЯ РАБОТА")}
                        </span>
                        {p.score_label && (
                          <span className="text-xs font-bold text-white">{p.score_label}</span>
                        )}
                      </div>
                      <h4 className="text-sm font-bold text-white mt-1">{p.title}</h4>
                      <p className="text-xs text-slate-400 mt-0.5">{p.filename || 'Работа.docx'}</p>
                    </div>

                    <div className="pt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => onTogglePortfolio(p.title, p.work_type, p.filename || undefined)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
                          p.is_starred
                            ? 'bg-amber-950 text-amber-300 border-amber-700/60'
                            : 'bg-slate-900 text-slate-300 border-slate-700 hover:border-slate-500'
                        }`}
                      >
                        {p.is_starred ? st("★ В портфолио") : st("☆ Добавить в портфолио")}
                      </button>
                      <button
                        type="button"
                        className="p-1.5 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-300 transition-all"
                        aria-label={st('Скачать')}
                      >
                        <DownloadIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {selectedComment && (
        <div className="cab-overlay">
          <div className="cab-shell cab-shell--wide cab-shell--tall bg-[#081524] border border-cyan-500/40 rounded-2xl max-w-md w-full p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-white">{st("Комментарий преподавателя")}</h3>
              <button
                type="button"
                onClick={() => setSelectedComment(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{selectedComment}</p>
            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedComment(null)}
                className="px-4 py-2 rounded-xl bg-cyan-950 hover:bg-cyan-900 text-cyan-300 border border-cyan-700 text-xs font-semibold"
              >
                {" " + st("Закрыть") + " "}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
